import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAuth, isStaff, isPresenter } from '../contexts/AuthContext';
import { fetchTestPapers } from '../services/testService';
import { flattenPaperQuestions, pickDefaultInterestPaper } from '../utils/testUtils';
import InterestClusterBarChart from '../components/widgets/InterestClusterBarChart';
import InterestClusterInfoModal from '../components/widgets/InterestClusterInfoModal';
import TestResultsAnalyticsSummary from '../components/widgets/TestResultsAnalyticsSummary';
import { topClusterScore } from '../utils/interestClusterDisplay';
import { isScoreableInterestVectorLength } from '../config/productVectors';
import { fetchInterestClusterScores } from '../utils/interestScoringApi';
import {
  fetchAllTestSubmissions,
  fetchTestSubmissionAnalytics,
  patchSubmissionClusterSnapshot
} from '../utils/testApi';
import { interestClusterTitle } from '../utils/interestClusterDisplay';

const PAGE_SIZE = 20;

const buildPageItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  if (currentPage <= 3) {
    [2, 3, 4].forEach((page) => pages.add(page));
  }
  if (currentPage >= totalPages - 2) {
    [totalPages - 3, totalPages - 2, totalPages - 1].forEach((page) => pages.add(page));
  }

  const sorted = [...pages].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  const items = [];
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) {
      items.push(`ellipsis-${sorted[index - 1]}`);
    }
    items.push(page);
  });
  return items;
};

function formatDateTime(d) {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString();
  } catch {
    return '';
  }
}

function normalizeAnswerValue(v) {
  if (v == null) return '—';
  if (typeof v === 'string') return v.trim() ? v : '—';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function completionReasonLabel(code, t) {
  if (code === 'submitted') return t('testResultsPage.completionSubmitted');
  if (code === 'timed_out') return t('testResultsPage.completionTimedOut');
  return code;
}

function sortQaEntries(entries) {
  return [...entries].sort(([a], [b]) => {
    const na = Number(String(a).replace(/\D/g, ''));
    const nb = Number(String(b).replace(/\D/g, ''));
    const aIsNum = Number.isFinite(na) && String(a).trim() !== '';
    const bIsNum = Number.isFinite(nb) && String(b).trim() !== '';
    if (aIsNum && bIsNum) return na - nb;
    if (aIsNum) return -1;
    if (bIsNum) return 1;
    return String(a).localeCompare(String(b));
  });
}

function choiceLabel(value, t) {
  const n = Number(value);
  if (n === 1) return t('testResultsPage.choiceLike');
  if (n === 0) return t('testResultsPage.choiceNeutral');
  if (n === -1) return t('testResultsPage.choiceDislike');
  return normalizeAnswerValue(value);
}

function choiceRowClass(value) {
  const n = Number(value);
  if (n === 1) return 'bg-emerald-50 text-emerald-900';
  if (n === 0) return 'bg-sky-50 text-sky-900';
  if (n === -1) return 'bg-rose-50 text-rose-900';
  return '';
}

function buildInterestRows(submission, questionMap) {
  const snapshot = Array.isArray(submission.interestSnapshot) ? submission.interestSnapshot : null;
  if (snapshot?.length) {
    return snapshot.map(({ interest_id, choice }) => ({
      id: interest_id,
      label: questionMap.get(interest_id) || interest_id,
      choice
    }));
  }
  const answers = submission.answers && typeof submission.answers === 'object' ? submission.answers : {};
  const order = Array.isArray(submission.selectedQuestionIds)
    ? submission.selectedQuestionIds
    : null;
  if (order?.length) {
    return order.map((id) => ({
      id,
      label: questionMap.get(id) || id,
      choice: answers[id]
    }));
  }
  return sortQaEntries(Object.entries(answers)).map(([id, choice]) => ({
    id,
    label: questionMap.get(id) || id,
    choice
  }));
}

function summarizeChoices(rows) {
  return rows.reduce(
    (acc, row) => {
      const n = Number(row.choice);
      if (n === 1) acc.like += 1;
      else if (n === 0) acc.neutral += 1;
      else if (n === -1) acc.dislike += 1;
      return acc;
    },
    { like: 0, neutral: 0, dislike: 0 }
  );
}

function ClusterResults({ clusters, t }) {
  const [modalCluster, setModalCluster] = useState(null);
  const [modalRank, setModalRank] = useState(1);

  if (!clusters?.length) return null;

  const topScore = topClusterScore(clusters);

  return (
    <div className="mt-4">
      <InterestClusterInfoModal
        cluster={modalCluster}
        rank={modalRank}
        topScore={topScore}
        isOpen={Boolean(modalCluster)}
        onClose={() => setModalCluster(null)}
      />
      <p className="text-sm font-semibold text-ink mb-2">{t('testResultsPage.clusterHeading')}</p>
      <InterestClusterBarChart clusters={clusters} limit={10} />
      {clusters.length > 10 ? (
        <details className="mt-3">
          <summary className="text-xs text-link cursor-pointer">{t('testResultsPage.allClusters')}</summary>
          <table className="w-full text-xs mt-2 border border-edge rounded-md overflow-hidden">
            <thead className="bg-surface-muted">
              <tr>
                <th className="text-left px-2 py-1">{t('testResultsPage.clusterTheme')}</th>
                <th className="text-right px-2 py-1">{t('testResultsPage.clusterScore')}</th>
              </tr>
            </thead>
            <tbody>
              {clusters.map((c, index) => (
                <tr key={`all-${c.cluster_id}-${c.theme}`} className="border-t border-edge">
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      onClick={() => {
                        setModalCluster(c);
                        setModalRank(index + 1);
                      }}
                      className="text-left text-link hover:underline font-medium"
                    >
                      {interestClusterTitle(c, t)}
                    </button>
                  </td>
                  <td className="px-2 py-1 text-right">{((c.score || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}

function SubmissionListToolbar({
  t,
  idPrefix,
  userIdFilter,
  onUserIdChange,
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  showPagination
}) {
  const [pageInput, setPageInput] = useState(String(page));

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const pageItems = showPagination ? buildPageItems(page, totalPages) : [];
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    onPageChange(nextPage);
  };

  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(page));
      return;
    }
    goToPage(Math.min(Math.max(1, parsed), totalPages));
  };

  const pageButtonClass = (active) =>
    `inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active ? 'bg-primary text-primary-foreground' : 'text-ink hover:bg-surface-muted'
    }`;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-edge bg-surface px-3 py-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="w-full sm:max-w-xs">
        <label className="block text-xs font-medium text-ink-muted mb-1">
          {t('testResultsPage.searchLabel')}
        </label>
        <input
          value={userIdFilter}
          onChange={(e) => onUserIdChange(e.target.value)}
          className="w-full rounded-md border border-edge-strong bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder={t('testResultsPage.searchPlaceholder')}
          aria-label={t('testResultsPage.searchLabel')}
        />
      </div>

      <div className="flex flex-col items-stretch gap-2 sm:items-end">
        <p className="text-xs text-ink-subtle sm:text-right">
          {t('testResultsPage.showingRange', { from, to, total })}
        </p>
        {showPagination ? (
          <div className="flex flex-wrap items-center gap-2">
            <nav className="flex items-center gap-1" aria-label={t('testResultsPage.pageOf', { page, total: totalPages })}>
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={t('testResultsPage.previousPage')}
                title={t('testResultsPage.previousPage')}
              >
                <FaChevronLeft className="h-3.5 w-3.5" aria-hidden />
              </button>
              {pageItems.map((item) =>
                typeof item === 'string' ? (
                  <span
                    key={item}
                    className="inline-flex h-8 min-w-8 items-center justify-center px-1 text-sm text-ink-subtle"
                    aria-hidden
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => goToPage(item)}
                    className={pageButtonClass(item === page)}
                    aria-label={t('testResultsPage.goToPage', { page: item })}
                    aria-current={item === page ? 'page' : undefined}
                  >
                    {item}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={t('testResultsPage.nextPage')}
                title={t('testResultsPage.nextPage')}
              >
                <FaChevronRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            </nav>
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                commitPageInput();
              }}
            >
              <label className="text-xs text-ink-muted whitespace-nowrap" htmlFor={`${idPrefix}-page`}>
                {t('testResultsPage.pageNumberLabel')}
              </label>
              <input
                id={`${idPrefix}-page`}
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={commitPageInput}
                className="w-16 rounded-md border border-edge-strong bg-surface px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={t('testResultsPage.pageNumberLabel')}
              />
              <span className="text-xs text-ink-subtle">/ {totalPages}</span>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function TestResults() {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const showSchoolScopeNotice = isStaff(currentUser) && !isPresenter(currentUser);
  const lang = i18n.language || 'en';
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [paperSlugFilter, setPaperSlugFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [paperTypeFilter, setPaperTypeFilter] = useState('interest');
  const [excludeInternalOnly, setExcludeInternalOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [interestPaper, setInterestPaper] = useState(null);
  const [clusterByKey, setClusterByKey] = useState({});
  const [scoringKey, setScoringKey] = useState(null);
  const [scoreErrorByKey, setScoreErrorByKey] = useState({});
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const papers = await fetchTestPapers('interest');
        if (!cancelled && Array.isArray(papers) && papers.length > 0) {
          setInterestPaper(pickDefaultInterestPaper(papers));
        }
      } catch {
        if (!cancelled) setInterestPaper(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const questionMap = useMemo(() => {
    const map = new Map();
    if (!interestPaper) return map;
    flattenPaperQuestions(interestPaper).forEach((item) => {
      const prompt = item.question?.prompt;
      const text =
        prompt && typeof prompt === 'object'
          ? prompt[lang] || prompt.en || prompt.tc || prompt.sc || item.question.id
          : item.question.id;
      map.set(item.question.id, text);
    });
    return map;
  }, [interestPaper, lang]);

  const filterParams = useMemo(
    () => ({
      paperSlug: paperSlugFilter.trim(),
      userId: userIdFilter.trim(),
      paperType: paperTypeFilter.trim(),
      excludeInternal: excludeInternalOnly ? '1' : '',
    }),
    [paperSlugFilter, userIdFilter, paperTypeFilter, excludeInternalOnly]
  );

  const handleUserIdChange = useCallback((value) => {
    setUserIdFilter(value);
    setPage(1);
  }, []);

  const handlePaperSlugChange = useCallback((value) => {
    setPaperSlugFilter(value);
    setPage(1);
  }, []);

  const handlePaperTypeChange = useCallback((value) => {
    setPaperTypeFilter(value);
    setPage(1);
  }, []);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllTestSubmissions({
        ...filterParams,
        page,
        limit: PAGE_SIZE
      });
      setSubmissions(result.data);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      if (page > result.totalPages) {
        setPage(result.totalPages);
      }
    } catch (e) {
      setError(e?.message || t('testResultsPage.loadFailed'));
      setSubmissions([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, t]);

  const showPresenterAnalytics = isPresenter(currentUser);

  const loadAnalytics = useCallback(async () => {
    if (!showPresenterAnalytics) {
      setAnalyticsSummary(null);
      return;
    }
    setAnalyticsLoading(true);
    try {
      const data = await fetchTestSubmissionAnalytics(filterParams);
      setAnalyticsSummary(data);
    } catch (e) {
      console.warn('Failed to load test analytics:', e?.message || e);
      setAnalyticsSummary(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [filterParams, showPresenterAnalytics]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const headingCount =
    showPresenterAnalytics && analyticsSummary?.total != null
      ? analyticsSummary.total
      : totalCount;

  const showPagination = totalPages > 1;
  const listToolbarProps = {
    t,
    userIdFilter,
    onUserIdChange: handleUserIdChange,
    page,
    totalPages,
    total: totalCount,
    pageSize: PAGE_SIZE,
    onPageChange: setPage,
    showPagination
  };

  const persistClusterSnapshot = useCallback(async (submission, key, clusters) => {
    if (!submission?._id || !clusters?.length) return;
    try {
      await patchSubmissionClusterSnapshot(submission._id, clusters);
      setSubmissions((prev) =>
        prev.map((row) => {
          const rowKey = row._id || `${row.userId}-${row.paperSlug}-${row.completedAt}`;
          return rowKey === key ? { ...row, clusterSnapshot: clusters } : row;
        })
      );
    } catch (e) {
      console.warn('Could not persist cluster snapshot:', e?.message || e);
    }
  }, []);

  const handleRescore = useCallback(async (submission, key) => {
    setScoringKey(key);
    setScoreErrorByKey((prev) => ({ ...prev, [key]: '' }));
    try {
      const interests = Array.isArray(submission.interestSnapshot)
        ? submission.interestSnapshot
        : undefined;
      const clusters = await fetchInterestClusterScores({
        interests,
        userId: submission.userId
      });
      setClusterByKey((prev) => ({ ...prev, [key]: clusters }));
      await persistClusterSnapshot(submission, key, clusters);
    } catch (e) {
      setScoreErrorByKey((prev) => ({
        ...prev,
        [key]: e?.message || t('testResultsPage.scoreFailed')
      }));
    } finally {
      setScoringKey(null);
    }
  }, [persistClusterSnapshot, t]);

  useEffect(() => {
    if (!expandedKey) return;
    const submission = submissions.find(
      (s) => (s._id || `${s.userId}-${s.paperSlug}-${s.completedAt}`) === expandedKey
    );
    if (!submission) return;
    const isInterest =
      submission.paperSlug === 'interest-test' || Array.isArray(submission.interestSnapshot);
    if (!isInterest) return;
    if (clusterByKey[expandedKey] || Array.isArray(submission.clusterSnapshot)) return;
    if (
      !Array.isArray(submission.interestSnapshot) ||
      !isScoreableInterestVectorLength(submission.interestSnapshot.length)
    ) {
      return;
    }
    handleRescore(submission, expandedKey);
  }, [expandedKey, submissions, clusterByKey, handleRescore]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">{t('navigation.testResults')}</h1>
          <p className="text-sm text-ink-muted">
            {headingCount === 1
              ? t('testResultsPage.submissionSingular', { count: headingCount })
              : t('testResultsPage.submissionPlural', { count: headingCount })}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full sm:w-auto">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">
              {t('testResultsPage.paperTypeLabel')}
            </label>
            <select
              value={paperTypeFilter}
              onChange={(e) => handlePaperTypeChange(e.target.value)}
              className="w-full sm:w-40 rounded-md border border-edge-strong bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="interest">{t('testResultsPage.paperTypeInterest')}</option>
              <option value="ability">{t('testResultsPage.paperTypeAbility')}</option>
              <option value="">{t('testResultsPage.paperTypeAll')}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">
              {t('testResultsPage.paperSlugLabel')}
            </label>
            <input
              value={paperSlugFilter}
              onChange={(e) => handlePaperSlugChange(e.target.value)}
              className="w-full sm:w-56 rounded-md border border-edge-strong bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t('testResultsPage.paperSlugPlaceholder')}
            />
          </div>
          <div className="flex items-end sm:col-span-2">
            <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={excludeInternalOnly}
                onChange={(e) => {
                  setExcludeInternalOnly(e.target.checked);
                  setPage(1);
                }}
                className="h-4 w-4 rounded border-edge-strong text-primary focus:ring-ring"
              />
              {t('testResultsPage.excludeInternalOnly')}
            </label>
          </div>
        </div>
      </div>

      {showPresenterAnalytics ? (
        <TestResultsAnalyticsSummary analytics={analyticsSummary} loading={analyticsLoading} />
      ) : null}

      {showSchoolScopeNotice ? (
        <p className="mt-4 text-sm text-ink-muted bg-accent/25 border border-blue-100 rounded-md px-4 py-3">
          {t('school.testResultsSchoolScope')}
        </p>
      ) : null}

      {error && (
        <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-900 text-sm">
          {error}
        </div>
      )}

      <div className="mt-6 space-y-3">
        <SubmissionListToolbar idPrefix="test-results-top" {...listToolbarProps} />

        {loading ? (
          <div className="bg-surface border border-edge rounded-lg p-6 text-ink-muted">
            {t('testResultsPage.loading')}
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-surface border border-edge rounded-lg p-6 text-ink-muted">
            {t('testResultsPage.emptyState')}
          </div>
        ) : (
          submissions.map((s) => {
            const key = s._id || `${s.userId}-${s.paperSlug}-${s.completedAt}`;
            const expanded = expandedKey === key;
            const isInterest =
              s.paperSlug === 'interest-test' ||
              Array.isArray(s.interestSnapshot) ||
              paperTypeFilter === 'interest';
            const interestRows = isInterest ? buildInterestRows(s, questionMap) : [];
            const summary = isInterest ? summarizeChoices(interestRows) : null;
            const clusters =
              clusterByKey[key] ||
              (Array.isArray(s.clusterSnapshot) ? s.clusterSnapshot : null);

            return (
              <div key={key} className="bg-surface border border-edge rounded-lg overflow-hidden">
                <button
                  type="button"
                  className="w-full text-left px-4 py-3 hover:bg-surface-muted flex items-start justify-between gap-3"
                  onClick={() => setExpandedKey(expanded ? null : key)}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p className="font-semibold text-ink break-all">{s.userId}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent/25 text-link border border-blue-100">
                        {s.paperSlug}
                      </span>
                      {s.completionReason ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-canvas text-ink-muted border border-edge">
                          {completionReasonLabel(s.completionReason, t)}
                        </span>
                      ) : null}
                      {s.isInternalAccountInput ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200">
                          {t('testResultsPage.internalAccountBadge')}
                        </span>
                      ) : null}
                      {summary ? (
                        <span className="text-xs text-ink-subtle">
                          +{summary.like} / 0:{summary.neutral} / −{summary.dislike}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-ink-muted mt-1">{formatDateTime(s.completedAt)}</p>
                  </div>
                  <div className="text-sm text-ink-subtle shrink-0">
                    {expanded ? t('testResultsPage.hide') : t('testResultsPage.view')}
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-edge px-4 py-4">
                    {isInterest && interestRows.length > 0 ? (
                      <>
                        <p className="text-sm font-semibold text-ink mb-2">
                          {t('testResultsPage.interestAnswersHeading')}
                        </p>
                        <div className="bg-canvas border border-edge rounded-md overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-surface-muted">
                              <tr>
                                <th className="text-left text-xs font-semibold text-ink-muted px-3 py-2 w-2/3">
                                  {t('testResultsPage.statementColumn')}
                                </th>
                                <th className="text-left text-xs font-semibold text-ink-muted px-3 py-2">
                                  {t('testResultsPage.answerColumn')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {interestRows.map((row) => (
                                <tr key={row.id} className={`border-t border-edge ${choiceRowClass(row.choice)}`}>
                                  <td className="align-top px-3 py-2 font-medium break-words">
                                    <span className="text-xs text-ink-subtle block">{row.id}</span>
                                    {row.label}
                                  </td>
                                  <td className="align-top px-3 py-2 break-words">
                                    {choiceLabel(row.choice, t)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {clusters?.length ? (
                          <ClusterResults clusters={clusters} t={t} />
                        ) : (
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              disabled={scoringKey === key}
                              onClick={() => handleRescore(s, key)}
                              className="text-sm px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                            >
                              {scoringKey === key
                                ? t('testResultsPage.scoring')
                                : t('testResultsPage.rescore')}
                            </button>
                            {scoreErrorByKey[key] ? (
                              <span className="text-sm text-rose-700">{scoreErrorByKey[key]}</span>
                            ) : (
                              <span className="text-sm text-ink-subtle">
                                {t('testResultsPage.noClusterSnapshot')}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-ink mb-2">
                          {t('testResultsPage.answersHeading')}
                        </p>
                        <div className="bg-canvas border border-edge rounded-md overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-surface-muted">
                              <tr>
                                <th className="text-left text-xs font-semibold text-ink-muted px-3 py-2 w-1/3">
                                  {t('testResultsPage.questionColumn')}
                                </th>
                                <th className="text-left text-xs font-semibold text-ink-muted px-3 py-2">
                                  {t('testResultsPage.answerColumn')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortQaEntries(Object.entries(s.answers || {})).map(([q, a]) => (
                                <tr key={q} className="border-t border-edge">
                                  <td className="align-top px-3 py-2 text-ink font-medium break-words">
                                    {q}
                                  </td>
                                  <td className="align-top px-3 py-2 text-ink break-words whitespace-pre-wrap">
                                    {normalizeAnswerValue(a)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        <SubmissionListToolbar idPrefix="test-results-bottom" {...listToolbarProps} />
      </div>
    </div>
  );
}
